(function () {
    'use strict';

    var page = document.getElementById('gallery-page');
    if (!page) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    page.classList.add('gallery-js');

    // Must stay in step with the same constants in gallery.php, which emits the
    // initial frame sizing server-side so cards do not flash the wrong shape.
    var CARD_RATIO = 1.6;
    var LANDSCAPE = 16 / 9;

    function frameBox(ratio) {
        var width = Math.min(1.06, (1.55 / CARD_RATIO) * ratio);
        return [width * 100, (width / ratio) * CARD_RATIO * 100];
    }

    function initReveal() {
        var items = Array.prototype.slice.call(page.querySelectorAll('.gallery-reveal'));
        if (!items.length) return;

        if (reduceMotion.matches || !('IntersectionObserver' in window)) {
            items.forEach(function (item) { item.classList.add('is-visible'); });
            return;
        }

        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
            });
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

        items.forEach(function (item) { observer.observe(item); });
    }

    function initSmoothCue() {
        var cue = page.querySelector('[data-gallery-scroll]');
        if (!cue) return;

        cue.addEventListener('click', function (event) {
            var href = cue.getAttribute('href') || '';
            if (href.charAt(0) !== '#') return;
            var target = page.querySelector(href);
            if (!target) return;
            event.preventDefault();
            var header = document.getElementById('header');
            var top = target.getBoundingClientRect().top + window.pageYOffset - (header ? header.offsetHeight : 0);
            window.scrollTo({ top: top, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
    }

    function initVideoGrid(openModal) {
        var grid = page.querySelector('.gallery-video-grid');
        if (!grid) return;

        var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-video-card]'));
        if (!cards.length) return;

        grid.addEventListener('error', function (event) {
            var image = event.target;
            if (!image.matches || !image.matches('img[data-fallback]') || image.dataset.fallbackApplied === 'true') return;
            image.dataset.fallbackApplied = 'true';
            image.src = image.dataset.fallback;
        }, true);

        grid.addEventListener('click', function (event) {
            var hit = event.target.closest('.gallery-video-card__hit');
            if (!hit) return;
            var card = hit.closest('[data-video-card]');
            if (card) openModal(card);
        });

        function applyRatio(card, ratio) {
            var box = frameBox(ratio);
            card.style.setProperty('--ratio', String(ratio));
            card.style.setProperty('--frame-w', box[0].toFixed(3) + '%');
            card.style.setProperty('--frame-h', box[1].toFixed(3) + '%');
            card.dataset.ratio = String(ratio);
        }

        // YouTube's standard thumbnail is always 1280x720 no matter the source,
        // so a video's real shape can only be read from the original-aspect-ratio
        // thumbnail. It 404s for ordinary uploads, which is the 16:9 answer.
        function probeRatio(card) {
            var id = card.dataset.videoId;
            if (!id) return;

            var probe = new Image();
            probe.decoding = 'async';
            probe.onload = function () {
                var width = probe.naturalWidth;
                var height = probe.naturalHeight;
                // A missing thumbnail can come back as a small grey placeholder
                // rather than a 404, so anything tiny counts as no answer.
                if (!width || !height || width < 200) return;

                var ratio = width / height;
                if (Math.abs(ratio - LANDSCAPE) < 0.02) return;

                applyRatio(card, Math.round(ratio * 10000) / 10000);
                var thumb = card.querySelector('.gallery-video-card__thumb');
                if (thumb) thumb.src = probe.src;
                card.classList.remove('needs-ratio');
            };
            probe.src = 'https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/oardefault.jpg';
        }

        var pending = cards.filter(function (card) { return card.classList.contains('needs-ratio'); });
        if (!pending.length) return;

        var idle = window.requestIdleCallback || function (callback) { return window.setTimeout(callback, 200); };
        idle(function () { pending.forEach(probeRatio); });
    }

    function initVideoModal() {
        var modal = document.querySelector('[data-video-modal]');
        if (!modal) return function () {};

        var dialog = modal.querySelector('.gallery-modal__dialog');
        var frame = modal.querySelector('[data-modal-frame]');
        var titleNode = modal.querySelector('[data-modal-title]');
        var closeButton = modal.querySelector('.gallery-modal__close');
        if (!dialog || !frame || !closeButton) return function () {};

        var expandButton = modal.querySelector('[data-modal-fullscreen]');
        var lastFocused = null;
        var closeTimer = null;

        // A fullscreen iframe is the only thing the browser paints, which puts
        // our own controls out of reach. So we take ownership of fullscreen and
        // promote the dialog instead — the close button is a descendant of it and
        // therefore stays visible and clickable.
        var requestFullscreen = dialog.requestFullscreen || dialog.webkitRequestFullscreen;
        var ownsFullscreen = !!requestFullscreen;

        function fullscreenElement() {
            return document.fullscreenElement || document.webkitFullscreenElement || null;
        }

        function exitFullscreen() {
            if (!fullscreenElement()) return;
            var exit = document.exitFullscreen || document.webkitExitFullscreen;
            if (exit) {
                try { exit.call(document); } catch (error) { /* already leaving */ }
            }
        }

        function syncFullscreenUi() {
            if (!expandButton) return;
            var active = fullscreenElement() === dialog;
            expandButton.classList.toggle('is-active', active);
            expandButton.setAttribute('aria-label', active ? 'Exit full screen' : 'Enter full screen');
        }

        function close() {
            if (modal.hidden) return;
            window.clearTimeout(closeTimer);
            // Leave fullscreen first: the browser must not be left presenting an
            // element we are about to tear out of the DOM.
            exitFullscreen();
            modal.classList.remove('is-open');
            document.body.classList.remove('gallery-modal-open');

            closeTimer = window.setTimeout(function () {
                modal.hidden = true;
                // Emptying the wrapper destroys the iframe, which is what stops
                // playback — pausing a cross-origin player is not possible here.
                frame.innerHTML = '';
            }, reduceMotion.matches ? 0 : 320);

            if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
            lastFocused = null;
        }

        function open(card) {
            var id = card.dataset.videoId;
            if (!id) return;

            window.clearTimeout(closeTimer);
            lastFocused = document.activeElement;

            var ratio = parseFloat(card.dataset.ratio);
            dialog.style.setProperty('--modal-ratio', String(ratio > 0 ? ratio : LANDSCAPE));

            var title = card.dataset.videoTitle || 'Video';
            if (titleNode) titleNode.textContent = title;

            var iframe = document.createElement('iframe');
            iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
                '?autoplay=1&rel=0&playsinline=1&modestbranding=1';
            iframe.title = title;
            iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
            if (!ownsFullscreen) {
                // No Element.requestFullscreen here (iOS Safari), so hand
                // fullscreen back to the player rather than losing it entirely.
                iframe.allow += '; fullscreen';
                iframe.setAttribute('allowfullscreen', '');
            }
            frame.innerHTML = '';
            frame.appendChild(iframe);

            modal.hidden = false;
            document.body.classList.add('gallery-modal-open');
            window.requestAnimationFrame(function () { modal.classList.add('is-open'); });
            closeButton.focus();
        }

        modal.addEventListener('click', function (event) {
            if (event.target.closest('[data-modal-dismiss]')) close();
        });

        if (expandButton && ownsFullscreen) {
            expandButton.hidden = false;
            expandButton.addEventListener('click', function () {
                if (fullscreenElement() === dialog) {
                    exitFullscreen();
                    return;
                }
                var result = requestFullscreen.call(dialog);
                if (result && typeof result.catch === 'function') result.catch(function () {});
            });

            ['fullscreenchange', 'webkitfullscreenchange'].forEach(function (eventName) {
                document.addEventListener(eventName, syncFullscreenUi);
            });
        }

        document.addEventListener('keydown', function (event) {
            if (modal.hidden || event.key !== 'Escape') return;
            // While fullscreen, Escape belongs to the browser: it should drop
            // back to the modal, not tear the whole player down.
            if (fullscreenElement()) return;
            event.preventDefault();
            close();
        });

        // Keep focus inside the dialog. Focus moving into the player iframe does
        // not surface here, so this only guards the surrounding page.
        document.addEventListener('focusin', function (event) {
            if (modal.hidden || fullscreenElement()) return;
            if (modal.contains(event.target)) return;
            closeButton.focus();
        });

        return open;
    }

    function initTestimonialRows() {
        var rows = Array.prototype.slice.call(page.querySelectorAll('[data-testimonial-row]'));
        if (!rows.length) return;

        // Both marquees are wide composited layers animating forever. Parking
        // them while the section is scrolled away frees the compositor.
        if ('IntersectionObserver' in window) {
            var visibility = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    entry.target.classList.toggle('is-offscreen', !entry.isIntersecting);
                });
            }, { rootMargin: '120px 0px' });

            rows.forEach(function (row) {
                row.classList.add('is-offscreen');
                visibility.observe(row);
            });
        }

        rows.forEach(function (row) {
            var viewport = row.querySelector('.gallery-testimonial-viewport');
            if (!viewport) return;

            viewport.addEventListener('pointerdown', function () { row.classList.add('is-interacting'); });
            ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (eventName) {
                viewport.addEventListener(eventName, function () { row.classList.remove('is-interacting'); });
            });
            viewport.addEventListener('touchstart', function () { row.classList.add('is-interacting'); }, { passive: true });
            viewport.addEventListener('touchend', function () { row.classList.remove('is-interacting'); }, { passive: true });
        });
    }

    initReveal();
    initSmoothCue();
    initVideoGrid(initVideoModal());
    initTestimonialRows();
})();
